import React, { useMemo, useRef, useState } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "../misc/Card";
import { Button } from "../misc/Button";
import { RadioGroup, RadioGroupItem } from "../misc/RadioGroup";
import { Label } from "../misc/Label";
import { Popover, PopoverContent, PopoverTrigger } from "../misc/Popover";
import { toast } from "react-toastify";
import CustomToast from "../misc/CustomToast";
import { cn } from "../../utils/classMerger";
import {
    checkInstagramWeeklyLimit,
    instagramLimitMessage,
} from "../../utils/instagramLimit";
import {
    type ChooseFrequencyProps,
    ContentLength,
    SocialPlatforms,
    weekDays,
} from "./types";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../misc/Select";
import { useIsMobile } from "../../hooks/useIsMobile";
import { FaClock } from "react-icons/fa";
import TimePicker from "../misc/TimePicker";
import { postTimeToTimeString, timeStringToPostTime } from "./utils/timeUtils";
import { Switch } from "../misc/Switch";
import PlatformFooterCard from "./utils/PlatformFooterCard";
import StepProgressBar from "../progress-bar";
import { getPlatformIcon } from "../../utils/logo-getter";
import { DAYS_OF_WEEK } from "../../utils/daysOfWeek";
import {
    checkPromotionWeeklyCap,
    PROMOTION_WEEKLY_CAP_MESSAGE,
} from "../../utils/promotionWeeklyCap";
import { platformTips } from "./utils/PlatformTips";
import { CONTENT_LENGTH_OPTIONS } from "./utils/contentLengths";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../listings/slideshow/Tooltip";
import { Info, X } from "lucide-react";
import CustomDialog from "../misc/CustomDialog";

// Helper function to get the formatted platform name.
const getPlatformName = (platformName: string) => {
    if (platformName.toLowerCase() === "twitter") {
        return "X (formerly Twitter)";
    } else {
        return platformName.charAt(0).toUpperCase() + platformName.slice(1);
    }
};

const MAX_POSTS_PER_DAY = {
    Facebook: 3,
    Instagram: 1,
    Twitter: 3,
    LinkedIn: 3,
    Pinterest: 3,
    Google: 1,
    YouTube: 3
};

// The initial campaign run is server-default `days = 14` (a 2-week period). The
// Pinterest image cap applies across the whole run, so scheduled-post estimates
// must span all 14 days — every weekday occurs exactly twice in that window.
const CAMPAIGN_INITIAL_DAYS = 14;
const CAMPAIGN_INITIAL_WEEKS = 2;

const ChooseFrequency: React.FC<ChooseFrequencyProps> = ({
    setCurrentStep,
    campaignPlatforms,
    setCampaignPlatforms,
    listingType,
    createCampaign,
    occupiedSlotsMapping,
    instagramPostsUsedByOthers,
    getAvailableTimeSlot,
    usableImageCount,
    isEditMode = false,
    sharedListing = false,
}) => {
    const [tooltipVisible, setTooltipVisible] = useState(false);
    // The footer card remounts per platform, so the "asked once per visit"
    // flag for the shared-listing footer-off warning lives here.
    const [footerOffAcknowledged, setFooterOffAcknowledged] = useState(false);

    const isMobile = useIsMobile(900);

    const containerRef = useRef<HTMLDivElement>(null);
    const topRef = useRef<HTMLDivElement>(null);

    // Track if the current platform configuration is valid.
    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    // New state to track which platform the user is configuring.
    const [currentPlatformIndex, setCurrentPlatformIndex] = useState<number>(0);

    const currentPlatform = campaignPlatforms[currentPlatformIndex];

    // The API pins the single Google My Business post to the campaign's first
    // day at postTimes[0] and never reads dayOfWeek or frequency for it, so the
    // day grid and the frequency radio are hidden here. Both values are still
    // carried in the payload - the campaign schema requires frequency.
    const isGoogle = currentPlatform?.name === SocialPlatforms.Google;

    // Pinterest posts one image per post and the server caps the whole run at the
    // number of usable images. Count scheduled posts across the full initial period
    // (not a single week) so the warning matches when the cap actually bites.
    const pinterestScheduledPosts = useMemo(() => {
        if (
            !currentPlatform ||
            currentPlatform.name !== SocialPlatforms.Pinterest
        ) {
            return 0;
        }
        return currentPlatform.frequency === "Weekly"
            ? currentPlatform.postsPerDay *
                  (currentPlatform.dayOfWeek?.length || 0) *
                  CAMPAIGN_INITIAL_WEEKS
            : currentPlatform.postsPerDay * CAMPAIGN_INITIAL_DAYS;
    }, [currentPlatform]);

    const showPinterestImageWarning =
        currentPlatform?.name === SocialPlatforms.Pinterest &&
        typeof usableImageCount === "number" &&
        usableImageCount > 0 &&
        pinterestScheduledPosts > usableImageCount;

    const platformProgressSteps = campaignPlatforms.map((platform, index) => ({
        label: getPlatformName(platform.name),
        isActive: index <= currentPlatformIndex,
        isCompleted: index < currentPlatformIndex,
    }));

    

    React.useEffect(() => {
        window.parent.scrollTo({ top: 0, behavior: "smooth" });
    }, [currentPlatformIndex]);

    const handleBack = () => {
        if (currentPlatformIndex > 0) {
            // Go back through the platforms first.
            setCurrentPlatformIndex((prevIndex) => prevIndex - 1);
        } else {
            // Once all platforms have been navigated, then move to the previous step.
            setCurrentStep((prevStep) =>
                prevStep > 0 ? prevStep - 1 : prevStep
            );
        }
    };

    // A manual Neighborhood Expert campaign posts at most twice a week on each
    // channel. The same gate sits on every schedule control and on Next, so
    // the picker never refuses what Next would accept or the other way round.
    // Autopilot-scheduled promotions never reach this step. Like the
    // Instagram limit, it guards the click and explains; nothing is grayed.
    const promotionCapBlocks = (
        platformName: string,
        frequency: "Daily" | "Weekly",
        dayOfWeek: string[] | undefined,
        postsPerDay: number,
    ): boolean => {
        if (listingType !== "Promotion") return false;
        const check = checkPromotionWeeklyCap(
            platformName,
            frequency,
            dayOfWeek,
            postsPerDay,
        );
        if (!check || check.allowed) return false;
        toast.error(
            <CustomToast message={PROMOTION_WEEKLY_CAP_MESSAGE} type="error" />,
            { autoClose: 8000 },
        );
        return true;
    };

    const nextHandler = () => {
        // MB-1455: account-wide Instagram weekly post limit. The edited
        // campaign is excluded upstream so it cannot count against itself;
        // every OTHER campaign's posts still cost, shared day or not.
        if (
            currentPlatform?.name === SocialPlatforms.Instagram &&
            currentPlatform?.frequency === "Weekly"
        ) {
            const limitCheck = checkInstagramWeeklyLimit(
                instagramPostsUsedByOthers,
                currentPlatform?.dayOfWeek,
                currentPlatform?.postsPerDay,
            );
            if (!limitCheck.allowed) {
                toast.error(
                    <CustomToast
                        message={instagramLimitMessage(limitCheck)}
                        type="error"
                    />,
                    { autoClose: 8000 },
                );
                return;
            }
        }
        // Also the last line of defence for a schedule prefilled from an
        // older campaign that was saved above the cap.
        if (
            currentPlatform &&
            promotionCapBlocks(
                currentPlatform.name,
                currentPlatform.frequency,
                currentPlatform.dayOfWeek,
                currentPlatform.postsPerDay,
            )
        ) {
            return;
        }
        if (!isDisabled) {
            if (currentPlatformIndex < campaignPlatforms.length - 1) {
                setCurrentPlatformIndex((prev) => prev + 1);
            } else {
                createCampaign();
            }
        } else {
            toast.error(
                <CustomToast
                    message="Please configure frequency settings for this platform."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
        }
    };

    // Update frequency for the current platform.
    const handleFrequencyChange = (value: "Daily" | "Weekly") => {
        // Force Instagram to always use Weekly
        const actualValue =
            currentPlatform.name === SocialPlatforms.Instagram
                ? "Weekly"
                : value;

        if (
            promotionCapBlocks(
                currentPlatform.name,
                actualValue,
                actualValue === "Weekly" ? currentPlatform.dayOfWeek : [],
                currentPlatform.postsPerDay,
            )
        ) {
            return;
        }

        setCampaignPlatforms((prev) =>
            prev.map((platform, index) =>
                index === currentPlatformIndex
                    ? {
                          ...platform,
                          frequency: actualValue,
                          // Clear days if switching to Daily.
                          dayOfWeek:
                              actualValue === "Weekly"
                                  ? platform.dayOfWeek || []
                                  : [],
                      }
                    : platform
            )
        );
    };

    // Toggle day selection for weekly frequency.
    const toggleDaySelection = (dayId: string) => {
        setCampaignPlatforms((prev) =>
            prev.map((platform, index) => {
                if (index === currentPlatformIndex) {
                    const currentDays = platform.dayOfWeek || [];

                    if (platform.name === SocialPlatforms.Google) {
                        const isSelected = currentDays.includes(dayId);
                        return {
                            ...platform,
                            dayOfWeek: isSelected ? [] : [dayId],
                        };
                    }

                    // If adding a day and platform is Instagram
                    if (
                        !currentDays.includes(dayId) &&
                        platform.name === SocialPlatforms.Instagram
                    ) {
                        // MB-1455: same rule the nextHandler guard applies,
                        // so the picker cannot refuse a day the Next button
                        // would have accepted.
                        const limitCheck = checkInstagramWeeklyLimit(
                            instagramPostsUsedByOthers,
                            [...currentDays, dayId],
                            platform.postsPerDay,
                        );
                        if (!limitCheck.allowed) {
                            toast.error(
                                <CustomToast
                                    message={instagramLimitMessage(limitCheck)}
                                    type="error"
                                />,
                                { autoClose: 8000 }
                            );
                            return platform;
                        }
                    }

                    if (
                        !currentDays.includes(dayId) &&
                        promotionCapBlocks(
                            platform.name,
                            platform.frequency,
                            [...currentDays, dayId],
                            platform.postsPerDay,
                        )
                    ) {
                        return platform;
                    }

                    return currentDays.includes(dayId)
                        ? {
                              ...platform,
                              dayOfWeek: currentDays.filter((d) => d !== dayId),
                          }
                        : { ...platform, dayOfWeek: [...currentDays, dayId] };
                }
                return platform;
            })
        );
    };

    const handlePostsPerDayChange = (platformName: string, value: string) => {
        const postsPerDay = Number.parseInt(value);
        setCampaignPlatforms((prevPlatforms) => {
            return prevPlatforms.map((platform) => {
                if (platform.name === platformName) {
                    if (
                        promotionCapBlocks(
                            platform.name,
                            platform.frequency,
                            platform.dayOfWeek,
                            postsPerDay,
                        )
                    ) {
                        return platform;
                    }
                    // Build a new postTimes array based on the new postsPerDay value
                    const daysToCheck =
                        platform.frequency === "Daily"
                            ? DAYS_OF_WEEK
                            : platform.dayOfWeek || [];
                    // Get current occupied set for this platform from the passed prop mapping.
                    const occSlots =
                        occupiedSlotsMapping[platformName] || new Set<string>();
                    const newPostTimes = Array.from(
                        { length: postsPerDay },
                        (_, index) => {
                            // If a post time already exists for this index, try to reuse it, otherwise, assign a new slot.
                            if (
                                platform.postTimes &&
                                index < platform.postTimes.length
                            ) {
                                return platform.postTimes[index];
                            }
                            // For the new post, choose a default base time (adjust as desired).
                            const defaultTime = { hour: 9 + index, minute: 0 };
                            return getAvailableTimeSlot(
                                occSlots,
                                defaultTime,
                                daysToCheck
                            );
                        }
                    );
                    // (Optionally, update contentLengths similarly.)
                    const options = CONTENT_LENGTH_OPTIONS[platformName] || [
                        "Short",
                    ];
                    const newContentLengths = Array.from(
                        { length: postsPerDay },
                        (_, index) => {
                            return platform.contentLengths &&
                                index < platform.contentLengths.length
                                ? platform.contentLengths[index]
                                : options[0];
                        }
                    );
                    return {
                        ...platform,
                        postsPerDay,
                        postTimes: newPostTimes,
                        contentLengths: newContentLengths,
                    };
                }
                return platform;
            });
        });
    };

    const handlePostTimeChange = (
        platformName: string,
        postIndex: number,
        value: string
    ) => {
        const newTime = timeStringToPostTime(value);
        // MB-1454: a cleared or half-typed box yields no time — keep the last
        // valid one rather than writing NaN, which serializes to null.
        if (!newTime) return;
        setCampaignPlatforms((prevPlatforms) => {
            if (!prevPlatforms) return prevPlatforms;
            return prevPlatforms.map((platformObj) => {
                if (platformObj.name === platformName) {
                    const updatedPostTimes = [...platformObj.postTimes];
                    updatedPostTimes[postIndex] = newTime;
                    return { ...platformObj, postTimes: updatedPostTimes };
                }
                return platformObj;
            });
        });
    };

    const handleContentLengthChange = (
        platformName: string,
        postIndex: number,
        value: ContentLength
    ) => {
        setCampaignPlatforms((prevPlatforms) => {
            if (!prevPlatforms) return prevPlatforms;
            return prevPlatforms.map((platformObj) => {
                if (platformObj.name === platformName) {
                    const updatedContentLengths = [
                        ...platformObj.contentLengths,
                    ];
                    updatedContentLengths[postIndex] = value;
                    return {
                        ...platformObj,
                        contentLengths: updatedContentLengths,
                    };
                }
                return platformObj;
            });
        });
    };

    React.useEffect(() => {
        const style = document.createElement("style");
        style.textContent = `
          .custom-time-input::-webkit-calendar-picker-indicator {
            background: none;
            display: none;
          }
          .custom-time-input::-webkit-datetime-edit-fields-wrapper {
            padding: 0;
          }
          .custom-time-input::-webkit-datetime-edit-hour-field,
          .custom-time-input::-webkit-datetime-edit-minute-field,
          .custom-time-input::-webkit-datetime-edit-ampm-field {
            padding: 0 4px;
          }
          .custom-time-input::-webkit-datetime-edit-hour-field:focus,
          .custom-time-input::-webkit-datetime-edit-minute-field:focus,
          .custom-time-input::-webkit-datetime-edit-ampm-field:focus {
            background-color: #E9C468;
            color: #193456;
          }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    React.useEffect(() => {
        const isWeeklyWithDays =
            isGoogle ||
            currentPlatform.frequency !== "Weekly" ||
            (currentPlatform.frequency === "Weekly" &&
                currentPlatform?.dayOfWeek?.length &&
                currentPlatform?.dayOfWeek?.length > 0);

        // MB-1455: the Instagram weekly-day limit no longer grays this button —
        // nextHandler guards the click and explains, so the user can adjust.
        if (isWeeklyWithDays) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }
    }, [currentPlatform, isGoogle]);

  React.useEffect(() => {
  // If current platform is YouTube or Promotion, force soldPost to false and set YouTube fields
  if (
    (listingType === "Promotion" || currentPlatform.name === "YouTube") &&
    (
      campaignPlatforms[currentPlatformIndex]?.soldPost !== false ||
      (currentPlatform.name === "YouTube" && (
        campaignPlatforms[currentPlatformIndex]?.youtubeVisibility !== "private" ||
        campaignPlatforms[currentPlatformIndex]?.youtubeNotifySubscribers !== false
      ))
    )
  ) {
    const updatedPlatforms = [...campaignPlatforms];
    updatedPlatforms[currentPlatformIndex] = {
      ...updatedPlatforms[currentPlatformIndex],
      soldPost: false,
    };
    setCampaignPlatforms(updatedPlatforms);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [listingType, currentPlatform.name, currentPlatformIndex]);

    return (
      <div className="container mx-auto p-4 max-w-4xl">
        <Card className="mb-6">
          {campaignPlatforms.length > 1 && (
            <div className="mt-4">
              <StepProgressBar
                steps={platformProgressSteps}
                isCurrent={isMobile}
              />
            </div>
          )}
          <CardHeader>
            <div className="flex items-center space-x-2">
              {getPlatformIcon(currentPlatform.name)}
              <CardTitle>
                {getPlatformName(currentPlatform.name)} Schedule Settings
              </CardTitle>

              <TooltipProvider>
                <Tooltip
                  {...(tooltipVisible
                    ? {
                        open: false /* force it closed when dialog is active */,
                      }
                    : undefined)}
                >
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setTooltipVisible(true)}
                    >
                      <Info className="h-4 w-4" />
                      <span className="sr-only">
                        Click here for {currentPlatform.name} Posting Tips.
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs text-white">
                    Click here for{" "}
                    {currentPlatform.name === "Twitter"
                      ? "X (formerly Twitter)"
                      : currentPlatform.name}{" "}
                    Posting Tips.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <CardDescription>
              {currentPlatform.name === SocialPlatforms.Google
                ? `
                                Configure the schedule for Google My Business by selecting the time to post.
                                Due to limitations with Google My Business, we will only send one post announcing the listing
                                on the campaign's first day, an open house post when scheduled, and a post upon sale if this
                                option is selected
                            `
                : currentPlatform.name === SocialPlatforms.Instagram
                  ? `
                                Configure the schedule for Instagram by selecting the days you want to post.
                                Due to limitations with Instagram, we will only send a maximum of 3 posts per week.
                            `
                  : ` 
                                Configure the schedule for${" "}
                                ${getPlatformName(
                                  currentPlatform.name,
                                )}. Choose "Daily"
                                to post every day, or select "Weekly" to pick specific
                                days.
                            `}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0" ref={containerRef}>
            <div ref={topRef}>
              {showPinterestImageWarning && (
                <div className="mb-4 flex items-start gap-2 rounded-md border border-[#ffbe00] bg-[#fff176]/30 p-3">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#d23600]" />
                  <p className="text-sm text-primary">
                    You have {usableImageCount} usable image
                    {usableImageCount === 1 ? "" : "s"} — Pinterest will post{" "}
                    {usableImageCount} time
                    {usableImageCount === 1 ? "" : "s"} instead of{" "}
                    {pinterestScheduledPosts}. Each Pinterest post uses a
                    different image.
                  </p>
                </div>
              )}
              {/* Frequency Selection */}
              {!isGoogle && (
                <div>
                  <Label className="text-primary font-semibold">Schedule</Label>
                  <RadioGroup
                    value={currentPlatform.frequency}
                    onValueChange={(value: string) =>
                      handleFrequencyChange(value as "Daily" | "Weekly")
                    }
                    className="grid grid-cols-2 gap-3"
                  >
                    <Label
                      htmlFor={`${currentPlatform.name}-daily`}
                      className={cn(
                        "flex items-center space-x-2 p-2 border rounded cursor-pointer",
                        currentPlatform.frequency === "Daily"
                          ? "bg-secondary text-white"
                          : "bg-white",
                        currentPlatform.name === SocialPlatforms.Instagram ||
                          currentPlatform.name === SocialPlatforms.Google
                          ? "opacity-50 cursor-not-allowed"
                          : "",
                      )}
                    >
                      <RadioGroupItem
                        value="Daily"
                        id={`${currentPlatform.name}-daily`}
                        disabled={
                          currentPlatform.name === SocialPlatforms.Instagram ||
                          currentPlatform.name === SocialPlatforms.Google
                        }
                      />
                      <span>Daily</span>
                    </Label>
                    <Label
                      htmlFor={`${currentPlatform.name}-weekly`}
                      className={cn(
                        "flex items-center space-x-2 p-2 border rounded cursor-pointer",
                        currentPlatform.frequency === "Weekly"
                          ? "bg-secondary text-white"
                          : "bg-white",
                      )}
                    >
                      <RadioGroupItem
                        value="Weekly"
                        id={`${currentPlatform.name}-weekly`}
                      />
                      <span>Weekly</span>
                    </Label>
                  </RadioGroup>
                </div>
              )}
              {/* Weekly Day Selection */}
              {!isGoogle && currentPlatform.frequency === "Weekly" && (
                <div>
                  <Label className="text-primary">Select Days</Label>
                  <div
                    className={`grid grid-cols-${
                      isMobile ? "2" : "3"
                    } gap-2 mt-2`}
                  >
                    {weekDays.map((day) => (
                      <div
                        key={day.id}
                        className={cn(
                          "p-2 border rounded cursor-pointer text-center",
                          currentPlatform.dayOfWeek &&
                            currentPlatform.dayOfWeek.includes(day.id)
                            ? "bg-secondary text-white"
                            : "bg-white",
                        )}
                        onClick={() => toggleDaySelection(day.id)}
                      >
                        {day.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {currentPlatform.name === SocialPlatforms.Instagram && (
                <div className="mt-2 text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                  <strong>Note:</strong> Instagram is limited to a maximum of 3
                  posts per week.
                </div>
              )}
              <div className="mt-2">
                {currentPlatform.name !== SocialPlatforms.Google ? (
                  <div className="mt-2">
                    <Label className="text-primary font-semibold">
                      Posts Per Day
                    </Label>
                    <Select
                      value={currentPlatform.postsPerDay.toString()}
                      onValueChange={(value) =>
                        handlePostsPerDayChange(currentPlatform.name, value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Posts per day" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from(
                          {
                            length: MAX_POSTS_PER_DAY[currentPlatform.name],
                          },
                          (_, index) => (
                            <SelectItem
                              key={index + 1}
                              value={(index + 1).toString()}
                            >
                              {index + 1} post
                              {index > 0 ? "s" : ""} per day
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}

                <div className="mt-2">
                  {isMobile ? (
                    <div className="space-y-4">
                      {Array.from(
                        {
                          length: currentPlatform.postsPerDay,
                        },
                        (_, index) => {
                          return (
                            <div key={index} className="p-4 border rounded-md">
                              <div className="flex justify-center  mb-2">
                                <div className="font-bold text-primary text-center">
                                  Post:
                                </div>

                                <div className="font-medium ml-2 text-center">
                                  Post {index + 1}
                                </div>
                              </div>
                              <div className="flex justify-center items-center space-x-4">
                                <div className="font-bold text-primary text-center">
                                  Time
                                </div>
                                <div className="relative flex justify-center items-center">
                                  <input
                                    type="time"
                                    className="custom-time-input w-[150px] pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E9C468] bg-white"
                                    value={postTimeToTimeString(
                                      currentPlatform.postTimes[index],
                                    )}
                                    onChange={(e) =>
                                      handlePostTimeChange(
                                        currentPlatform.name,
                                        index,
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="absolute left-1.5 top-1/2 transform -translate-y-1/2 text-gray-400 bg-transparent hover:bg-transparent hover:text-secondary"
                                      >
                                        <FaClock className="h-4 w-4" />
                                        <span className="sr-only">
                                          Open time picker
                                        </span>
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                      className="w-auto p-0"
                                      align="start"
                                    >
                                      <TimePicker
                                        time={postTimeToTimeString(
                                          currentPlatform.postTimes[index],
                                        )}
                                        onChange={(newTimeStr) =>
                                          handlePostTimeChange(
                                            currentPlatform.name,
                                            index,
                                            newTimeStr,
                                          )
                                        }
                                      />
                                    </PopoverContent>
                                  </Popover>
                                </div>
                              </div>

                              <div className="font-bold text-primary text-center mt-2">
                                Content Length
                              </div>
                              <div>
                                <div className="flex items-center justify-center space-x-2">
                                  {CONTENT_LENGTH_OPTIONS[
                                    currentPlatform.name
                                  ].map((option) => (
                                    <label
                                      key={option}
                                      className={`flex items-center justify-center px-3 py-1 rounded-full text-sm cursor-pointer transition-colors ${
                                        currentPlatform.contentLengths[
                                          index
                                        ] === option
                                          ? "bg-secondary text-white"
                                          : "bg-muted hover:bg-lightSecondary"
                                      }`}
                                    >
                                      <input
                                        type="radio"
                                        name={`${currentPlatform.name}-post-${index}-length`}
                                        value={option}
                                        checked={
                                          currentPlatform.contentLengths[
                                            index
                                          ] === option
                                        }
                                        onChange={() =>
                                          handleContentLengthChange(
                                            currentPlatform.name,
                                            index,
                                            option as ContentLength,
                                          )
                                        }
                                        className="sr-only"
                                      />
                                      {option}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-[100px_300px_1fr] gap-x-4">
                      {/* Header */}
                      <div className="w-[100px] font-bold pb-2 text-primary text-center">
                        Post
                      </div>
                      <div className="w-[250px] font-bold pb-2 text-primary text-center">
                        Time
                      </div>
                      <div className="font-bold pb-2 text-primary text-center">
                        Content Length
                      </div>
                      {Array.from(
                        {
                          length: currentPlatform.postsPerDay,
                        },
                        (_, index) => {
                          return (
                            <React.Fragment key={index}>
                              <div className="w-[100px] font-medium pt-2 text-center">
                                Post {index + 1}
                              </div>
                              <div className="pt-2 flex justify-center">
                                <div className="relative flex justify-center items-center">
                                  <input
                                    type="time"
                                    className="custom-time-input w-[150px] pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E9C468] bg-white"
                                    value={postTimeToTimeString(
                                      currentPlatform.postTimes[index],
                                    )}
                                    onChange={(e) =>
                                      handlePostTimeChange(
                                        currentPlatform.name,
                                        index,
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="absolute left-1.5 top-1/2 transform -translate-y-1/2 text-gray-400 bg-transparent hover:bg-transparent hover:text-secondary"
                                      >
                                        <FaClock className="h-4 w-4" />
                                        <span className="sr-only">
                                          Open time picker
                                        </span>
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                      className="w-auto p-0"
                                      align="start"
                                    >
                                      <TimePicker
                                        time={postTimeToTimeString(
                                          currentPlatform.postTimes[index],
                                        )}
                                        onChange={(newTimeStr) =>
                                          handlePostTimeChange(
                                            currentPlatform.name,
                                            index,
                                            newTimeStr,
                                          )
                                        }
                                      />
                                    </PopoverContent>
                                  </Popover>
                                </div>
                              </div>
                              <div className="pt-2">
                                <div className="flex items-center justify-center space-x-4">
                                  {CONTENT_LENGTH_OPTIONS[
                                    currentPlatform.name
                                  ].map((option) => (
                                    <label
                                      key={option}
                                      className={`flex items-center justify-center px-3 py-1 rounded-full text-sm cursor-pointer transition-colors ${
                                        currentPlatform.contentLengths[
                                          index
                                        ] === option
                                          ? "bg-secondary text-white"
                                          : "bg-muted hover:bg-lightSecondary"
                                      }`}
                                    >
                                      <input
                                        type="radio"
                                        name={`${currentPlatform.name}-post-${index}-length`}
                                        value={option}
                                        checked={
                                          currentPlatform.contentLengths[
                                            index
                                          ] === option
                                        }
                                        onChange={() =>
                                          handleContentLengthChange(
                                            currentPlatform.name,
                                            index,
                                            option as ContentLength,
                                          )
                                        }
                                        className="sr-only"
                                      />
                                      {option}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            </React.Fragment>
                          );
                        },
                      )}
                    </div>
                  )}
                  {currentPlatform.name === SocialPlatforms.Instagram ? (
                    <div className="mt-2 text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                      <strong>Note:</strong> Instagram is limited to a maximum
                      of 1 post per day and restricted to Short content length.
                      This helps protect your instagram from getting suspended
                      for over posting.
                    </div>
                  ) : null}
                </div>

                <div className="mt-6 w-full flex flex-col justify-center items-start space-y-4">
                  {listingType !== "Promotion" &&
                  currentPlatform.name !== "YouTube" ? (
                    <div className="flex flex-row justify-between items-center w-full">
                      <Label
                        htmlFor="sold-post"
                        className="text-primary flex flex-col space-y-1 font-semibold"
                      >
                        <span>Create Sold Post</span>
                        <span className="text-black font-normal text-sm text-muted-foreground">
                          Automatically create and schedule a post when a
                          listing is sold
                        </span>
                      </Label>
                      <Switch
                        id="sold-post"
                        checked={
                          campaignPlatforms[currentPlatformIndex]?.soldPost ??
                          true
                        }
                        onCheckedChange={(checked: boolean) => {
                          const updatedPlatforms = [...campaignPlatforms];
                          updatedPlatforms[currentPlatformIndex] = {
                            ...updatedPlatforms[currentPlatformIndex],
                            soldPost: checked,
                          };
                          setCampaignPlatforms(updatedPlatforms);
                        }}
                      />
                    </div>
                  ) : null}

                  {currentPlatform.name === "YouTube" && (
                    <div className="flex flex-col w-full gap-4">
                      {/* YouTube Visibility Toggle */}
                      <div className="flex flex-row justify-between items-center w-full">
                        <Label
                          htmlFor="youtube-visibility"
                          className="text-primary flex flex-col space-y-1 font-semibold"
                        >
                          <span>YouTube Video Visibility</span>
                          <span className="text-black font-normal text-sm text-muted-foreground">
                            Set your YouTube video as Public by
                            default.
                          </span>
                        </Label>
                        <Switch
                          id="youtube-visibility"
                          checked={
                            (campaignPlatforms[currentPlatformIndex]
                              ?.youtubeVisibility ?? "public") === "public"
                          }
                          onCheckedChange={(checked: boolean) => {
                            const updatedPlatforms = [...campaignPlatforms];
                            updatedPlatforms[currentPlatformIndex] = {
                              ...updatedPlatforms[currentPlatformIndex],
                              youtubeVisibility: checked ? "public" : "private",
                            };
                            setCampaignPlatforms(updatedPlatforms);
                          }}
                        />
                      </div>

                      {/* YouTube Notify Subscribers Toggle */}
                      <div className="flex flex-row justify-between items-center w-full">
                        <Label
                          htmlFor="youtube-notify"
                          className="text-primary flex flex-col space-y-1 font-semibold"
                        >
                          <span>Notify Subscribers</span>
                          <span className="text-black font-normal text-sm text-muted-foreground">
                            Notify your YouTube subscribers when this video is
                            published.
                          </span>
                        </Label>
                        <Switch
                          id="youtube-notify"
                          checked={
                            campaignPlatforms[currentPlatformIndex]
                              ?.youtubeNotifySubscribers ?? true
                          }
                          onCheckedChange={(checked: boolean) => {
                            const updatedPlatforms = [...campaignPlatforms];
                            updatedPlatforms[currentPlatformIndex] = {
                              ...updatedPlatforms[currentPlatformIndex],
                              youtubeNotifySubscribers: checked,
                            };
                            setCampaignPlatforms(updatedPlatforms);
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex flex-row justify-between items-center w-full">
                    <Label
                      htmlFor="use-emojis"
                      className="text-primary flex flex-col space-y-1 font-semibold"
                    >
                      <span>Use Emojis in Posts</span>
                      <span className="text-black font-normal text-sm text-muted-foreground">
                        {currentPlatform.name === SocialPlatforms.YouTube
                          ? "Include emojis in AI-generated descriptions."
                          : "Include emojis in your social media posts"}
                      </span>
                    </Label>
                    <Switch
                      id="use-emojis"
                      checked={
                        campaignPlatforms[currentPlatformIndex]?.emojis ?? true
                      }
                      onCheckedChange={(checked: boolean) => {
                        const updatedPlatforms = [...campaignPlatforms];
                        updatedPlatforms[currentPlatformIndex] = {
                          ...updatedPlatforms[currentPlatformIndex],
                          emojis: checked,
                        };
                        setCampaignPlatforms(updatedPlatforms);
                      }}
                    />
                  </div>

                  {/* Use a Photo Toggle */}
                  {listingType === "Promotion" && (
                    <div className="flex flex-row justify-between items-center w-full mt-2">
                      <Label
                        htmlFor="use-photo"
                        className="text-primary flex flex-col space-y-1 font-semibold"
                      >
                        <span>Use a Photo</span>
                        <span className="text-black font-normal text-sm text-muted-foreground">
                          Include a photo with each promotional post
                        </span>
                      </Label>
                      {currentPlatform.name === SocialPlatforms.Instagram ? (
                        <Switch id="use-photo" checked={true} disabled={true} />
                      ) : (
                        <Switch
                          id="use-photo"
                          checked={
                            campaignPlatforms[currentPlatformIndex]?.useImage ??
                            true
                          }
                          onCheckedChange={(checked: boolean) => {
                            const updatedPlatforms = [...campaignPlatforms];
                            updatedPlatforms[currentPlatformIndex] = {
                              ...updatedPlatforms[currentPlatformIndex],
                              useImage: checked,
                            };
                            setCampaignPlatforms(updatedPlatforms);
                          }}
                        />
                      )}
                    </div>
                  )}
                  {listingType === "Promotion" &&
                    currentPlatform.name === SocialPlatforms.Instagram && (
                      <div className="mt-1 text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200 w-full">
                        <strong>Note:</strong> Instagram requires a photo for
                        all posts.
                      </div>
                    )}

                  {listingType !== "Promotion" ? (
                    <div className="flex flex-row justify-between items-center w-full">
                      <Label
                        htmlFor="use-price"
                        className="text-primary flex flex-col space-y-1 font-semibold"
                      >
                        <span>Use Price in Posts</span>
                        <span className="text-black font-normal text-sm text-muted-foreground">
                          Include price in your social media posts if available
                        </span>
                      </Label>
                      <Switch
                        id="use-price"
                        checked={
                          campaignPlatforms[currentPlatformIndex]?.usePrice ??
                          true
                        }
                        onCheckedChange={(checked: boolean) => {
                          const updatedPlatforms = [...campaignPlatforms];
                          updatedPlatforms[currentPlatformIndex] = {
                            ...updatedPlatforms[currentPlatformIndex],
                            usePrice: checked,
                          };
                          setCampaignPlatforms(updatedPlatforms);
                        }}
                      />
                    </div>
                  ) : null}
                </div>

                {
                  <div className="mt-6">
                    <PlatformFooterCard
                      key={currentPlatform.name}
                      platform={currentPlatform}
                      setPlatforms={setCampaignPlatforms}
                      sharedListing={sharedListing}
                      footerOffAcknowledged={footerOffAcknowledged}
                      onFooterOffAcknowledged={() =>
                        setFooterOffAcknowledged(true)
                      }
                    />
                  </div>
                }

                {listingType !== "Promotion" &&
                  currentPlatform.name === SocialPlatforms.Twitter && (
                    <div className="mt-2 text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                      <strong>Note:</strong> Footer information is included in
                      the first post and then every third post after that. This
                      helps avoid X's (formerly Twitter's) spam detection, which
                      can flag posts with repeated identical text.
                    </div>
                  )}
                {listingType !== "Promotion" &&
                  currentPlatform.name === SocialPlatforms.YouTube && (
                    <div className="mt-2 text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                      <strong>Note:</strong> YouTube limits the number of videos
                      that can be uploaded per day via API. Individual listings
                      are limited to 3 posts per day.
                    </div>
                  )}
              </div>
              {/* Navigation Buttons */}
              <div className="mt-8 flex justify-between">
                <Button variant="secondary" onClick={handleBack}>
                  {currentPlatformIndex === 0 ? "Back" : "Previous"}
                </Button>
                <Button onClick={nextHandler} disabled={isDisabled}>
                  {currentPlatformIndex < campaignPlatforms.length - 1
                    ? "Next"
                    : isEditMode
                      ? "Save Changes"
                      : "Create Campaign"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <CustomDialog
          open={tooltipVisible}
          onClose={() => setTooltipVisible(false)}
          className="max-w-4xl w-full"
        >
          <div className="max-w-4xl min-w-4xl bg-white">
            <h2 className="text-xl font-bold mb-4 text-primary text-center">
              {currentPlatform.name === "Twitter"
                ? "X (formerly Twitter)"
                : currentPlatform.name}{" "}
              Posting Tips
            </h2>
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-red-500"
              onClick={() => setTooltipVisible(false)}
            >
              <X className="h-6 w-6" />
            </button>
            <div className="space-y-4 text-gray-700 text-sm">
              {platformTips[currentPlatform.name] || (
                <p>
                  No info available for{" "}
                  {currentPlatform.name === "Twitter"
                    ? "X (formerly Twitter)"
                    : currentPlatform.name}
                  .
                </p>
              )}
            </div>
          </div>
        </CustomDialog>
      </div>
    );
};

export default ChooseFrequency;
